import { getLangConf } from "@/i18n";
import { FormatToBindPointParams, Point } from "@/types/crypto";

import {
  CURRENCY_NAME,
  CURRENCY_SYMBOL,
  POINT_RATIO,
  POINT_TYPE,
} from "../constants/constants.ts";
import {
  baseToBind,
  baseToPoint,
  baseToReward,
  getCrypto,
  getPointFixed,
} from "../stores/cryptoStore.ts";
import {
  getBindPointFixed,
  getDefaultPointType,
  getRewardPointType,
} from "../stores/userStore.ts";

export const MinCrypto = 200;
export const MinPlatCoin = 801;
// 根据 point_type 判断是否是虚拟币
export function isCrypto(point_type: number) {
  return point_type > MinCrypto && point_type < MinPlatCoin;
}

// 根据 point_type 判断是否是法币
export function isFiat(point_type: number) {
  return point_type <= MinCrypto;
}

export function getCurPointType() {
  return POINT_TYPE; //默认配置 5 巴西 6 墨西哥
}

export function getCurPointSymbol() {
  return CURRENCY_SYMBOL; //默认配置
}

export function getCurrencyName() {
  return CURRENCY_NAME; //默认配置
}

//获取汇率
export function getPointRatio() {
  return POINT_RATIO; //默认配置
}

export function pointFormatter(
  point: number,
  point_type: number = 5,
  point_ratio: number = 10000,
  type = 3,
  defaultFix = 2,
  bDeleteZeroPoint = false,
  showUnit = false,
) {
  point_ratio = point_ratio ? point_ratio : getPointRatio();
  return pointFormatterInFiat(
    pointFormatterOrigin(point, point_type, point_ratio, type),
    point_type,
    point_ratio,
    defaultFix,
    bDeleteZeroPoint,
    showUnit,
  );
}

//按en-US格式化金额： 10000=>1.00  $cp
export function pointFormatterOrigin(
  point: number,
  point_type: number = 5,
  point_ratio: number = 10000,
  type = 3,
) {
  point_ratio = point_ratio ? point_ratio : getPointRatio();
  if (!point) return 0;
  const defaultFix = `${point_ratio}`.length;
  const factor = Math.pow(10, defaultFix); // 放大系数 决定了保留多少小数位取整;
  let result = 0;
  const plus = point > 0 ? 1 : -1; // debug记录正负, 处理负值情况
  point = Math.abs(point);
  switch (type) {
    case 1:
      result = (plus * Math.round((point * factor) / point_ratio)) / factor;
      break;
    case 2:
      result = (plus * Math.ceil((point * factor) / point_ratio)) / factor;
      break;
    case 3:
      result = (plus * Math.floor((point * factor) / point_ratio)) / factor;
      break;
    default:
      result = (plus * Math.round((point * factor) / point_ratio)) / factor;
      break;
  }
  return parseFloat(`${result}`);
}

//按en-US格式化金额： 10000=>1.00  $cp
export function pointFormatterOriginK(
  point: number,
  point_ratio: number = 10000,
  type = 3,
) {
  point_ratio = point_ratio ? point_ratio : getPointRatio();
  if (!point) return 0;
  const defaultFix = `${point_ratio}`.length;
  const factor = Math.pow(10, defaultFix); // 放大系数 决定了保留多少小数位取整;
  let result = 0;
  const plus = point > 0 ? 1 : -1; // debug记录正负, 处理负值情况
  point = Math.abs(point);
  switch (type) {
    case 1:
      result = (plus * Math.round((point * factor) / point_ratio)) / factor;
      break;
    case 2:
      result = (plus * Math.ceil((point * factor) / point_ratio)) / factor;
      break;
    case 3:
      result = (plus * Math.floor((point * factor) / point_ratio)) / factor;
      break;
    default:
      result = (plus * Math.round((point * factor) / point_ratio)) / factor;
      break;
  }
  return parseFloat(`${result / 1000}`);
}

//法币格式化 $sp
export function pointFormatterInFiat(
  value: number,
  point_type: number = 5,
  point_ratio: number = 10000,
  defaultFix = 2,
  bDeleteZeroPoint = false,
  showUnit = false,
) {
  if (!value) {
    value = 0;
  }
  point_ratio = point_ratio ? point_ratio : getPointRatio();
  const isRenderK = showUnit && value >= 1000;
  if (isRenderK) {
    const rate = Math.pow(10, defaultFix);
    value = Math.floor((value / 1000) * rate) / rate;
    //以K为单位——保留2位小数但是删除小数点后的0
    bDeleteZeroPoint = true;
    defaultFix = 2;
  } else {
    const pointRatio = point_ratio;
    if (pointRatio <= 10) {
      //低价值货币——不保留小数
      defaultFix = 0;
      bDeleteZeroPoint = false;
    }
  }
  let result = pointFormatterLocal(value, defaultFix, bDeleteZeroPoint);

  if (isRenderK) {
    result += "k";
  }
  return result;
}
//按本地化进行金额格式化  $fp
export function pointFormatterLocal(
  value: number | string,
  defaultFix = 2,
  bDeleteZeroPoint = false,
) {
  if (value == null || value == "") {
    value = 0;
  }
  value = parseFloat(String(value)) || 0;
  //格式化至对应数据
  if (defaultFix == 0) {
    value = String(parseInt(String(value)));
  } else {
    value = value + "";
    const decimalIndex = value.indexOf("."); //是否有小数
    if (decimalIndex > -1) {
      value = parseFloat(
        value.substring(0, decimalIndex + defaultFix + 1),
      ).toFixed(defaultFix);
    } else {
      value = parseFloat(value).toFixed(defaultFix);
    }
  }

  if (defaultFix > 2) {
    for (let i = 2; i < defaultFix; i++) {
      const floatValue = parseFloat(value).toFixed(i);
      if (parseFloat(value) == parseFloat(floatValue)) {
        value = floatValue;
        break;
      }
    }
  }
  const decimalPoint = getPointFormatConf().decimalPoint; //formatType0时 默认小数分隔符
  const thousandsSymbol = getPointFormatConf().thousandsSymbol; //formatType0时 默认千分位分割符
  let num = ""; //结果

  const decimalIndex = value.indexOf("."); //是否有小数 和前方decimalIndex不一定同结果
  let decimalValue = "";
  let integerValue = value;
  if (decimalIndex > -1) {
    integerValue = value.substring(0, decimalIndex);
    let findDecaimalValue = value.substring(decimalIndex, value.length);
    //小数点后全为0 bDeleteZeroPoint = true时删除不展示后面的0
    if (bDeleteZeroPoint && parseFloat(findDecaimalValue) == 0) {
      findDecaimalValue = "";
    }
    if (findDecaimalValue) {
      decimalValue = findDecaimalValue.replace(".", decimalPoint);
    }
  }
  //分割组合组合结果
  const len = integerValue.length;
  for (let i = len; i > 0; i -= 3) {
    num = thousandsSymbol + integerValue.substring(i - 3, i) + num;
  }
  num = num.replace(thousandsSymbol, "");
  num += decimalValue;
  return num || "";
}

export function getPointFormatConf() {
  const formatType = getLangConf()?.thousandths || 0;
  //默认0的配置
  const conf = {
    decimalPoint: ".",
    thousandsSymbol: ",",
  };
  if (formatType == 1) {
    conf.decimalPoint = ",";
    conf.thousandsSymbol = ".";
  } else if (formatType == 2) {
    conf.decimalPoint = ",";
    conf.thousandsSymbol = " ";
  }
  return conf;
}

//格式化金额处理
export function fpPoint(
  value: number | string,
  defaultFix = 2,
  bDeleteZeroPoint = false,
) {
  if (value == null || value == "") {
    value = 0;
  }
  value = String(parseFloat(String(value)) || 0);
  //格式化至对应数据
  if (defaultFix == 0) {
    value = String(parseInt(value));
  } else {
    value = String(value);
    const decimalIndex = value.indexOf("."); //是否有小数
    if (decimalIndex > -1) {
      value = parseFloat(
        value.substring(0, decimalIndex + defaultFix + 1),
      ).toFixed(defaultFix);
    } else {
      value = parseFloat(value).toFixed(defaultFix);
    }
  }

  if (defaultFix > 2) {
    for (let i = 2; i < defaultFix; i++) {
      const floatValue = parseFloat(value).toFixed(i);
      if (parseFloat(value) == parseFloat(floatValue)) {
        value = floatValue;
        break;
      }
    }
  }
  const decimalPoint = getPointFormatConf().decimalPoint; //formatType0时 默认小数分隔符
  const thousandsSymbol = getPointFormatConf().thousandsSymbol; //formatType0时 默认千分位分割符
  let num = ""; //结果

  const decimalIndex = value.indexOf("."); //是否有小数 和前方decimalIndex不一定同结果
  let decimalValue = "";
  let integerValue = value;
  if (decimalIndex > -1) {
    integerValue = value.substring(0, decimalIndex);
    let findDecaimalValue = value.substring(decimalIndex, value.length);
    //小数点后全为0 bDeleteZeroPoint = true时删除不展示后面的0
    if (bDeleteZeroPoint && parseFloat(findDecaimalValue) == 0) {
      findDecaimalValue = "";
    }
    if (findDecaimalValue) {
      decimalValue = findDecaimalValue.replace(".", decimalPoint);
    }
  }
  //分割组合组合结果
  const len = integerValue.length;
  for (let i = len; i > 0; i -= 3) {
    num = thousandsSymbol + integerValue.substring(i - 3, i) + num;
  }
  num = num.replace(thousandsSymbol, "");
  num += decimalValue;
  return num || "";
}

//处理奖励金额相关
export function renderFiatOrPlatCoinSymbol(
  point: number | undefined,
  isConverted = false,
  bDeleteZeroPoint = false,
  decimals = -1,
  unit = "",
  isSymbol = true,
) {
  point = point || 0;
  let valueStr = "";
  const fixed = decimals > -1 ? decimals : getPointFixed(getRewardPointType());
  if (isConverted) {
    valueStr = pointFormatterLocal(point, fixed, bDeleteZeroPoint);
  } else {
    valueStr = pointFormatterLocal(
      baseToPoint(point, getRewardPointType()),
      fixed,
      bDeleteZeroPoint,
    );
  }
  if (isSymbol) {
    const isFiatType = isFiat(getRewardPointType());
    if (isFiatType) {
      return getCrypto(getRewardPointType()).symbol + " " + valueStr + unit;
    } else {
      return valueStr + unit + " " + getCrypto(getRewardPointType()).name;
    }
  } else {
    return valueStr;
  }
}
//基础货币转玩家绑定的法币币种或者系统默认配置法币币种，适应于：活动配置下发的奖励金额
// 是否需要货币符号symbol 货币名字name 货币icon
/** Point:基础货币金额 isConverted:是否已转过绑定法币 bDeleteZeroPoint:删除不展示后面的0 * */
export function formatBaseToBind(
  point: number,
  isConverted = false,
  bDeleteZeroPoint = false,
) {
  let formatValue = "";
  if (isConverted) {
    formatValue = pointFormatterLocal(
      point,
      getBindPointFixed(),
      bDeleteZeroPoint,
    );
  } else {
    formatValue = pointFormatterLocal(
      baseToBind(point),
      getBindPointFixed(),
      bDeleteZeroPoint,
    );
  }

  const cryptoCoinInfo = getCrypto(getDefaultPointType());

  const resPoint: Point = {
    value: formatValue,
    symbol: cryptoCoinInfo.symbol,
    name: cryptoCoinInfo.name,
    icon: cryptoCoinInfo.icon,
  };
  return resPoint;
}

//常用显示：
/**
 * Point:基础货币金额 isShowSymbol:是否显示货币符号: 法币 符号 + 金额，非法币：金额+符号 isForward:
 * 虚拟币的情况下，是否将符号显示在金额前面 isShowName:是否显示货币名称: 法币 符号 + 金额，非法币：金额+符号
 * isNeedSpace:金额和符号之间是否需要空格 isConverted:是否已转过绑定法币 bDeleteZeroPoint:删除不展示后面的0
 *
 * -
 */
export function renderBaseToBind(
  point: number,
  isShowSymbol = true,
  isForwardShowSymbol = false,
  isShowName = false,
  isNeedSpace = false,
  isConverted = false,
  bDeleteZeroPoint = false,
) {
  const formatPointInfo = formatBaseToBind(
    point,
    isConverted,
    bDeleteZeroPoint,
  );
  // console.log(
  //   "[renderBaseToBind] formatPointInfo  isShowSymbol  isShowName",
  //   JSON.stringify(formatPointInfo),
  //   isShowSymbol,
  //   isShowName
  // );
  const spaceFlag = isNeedSpace ? " " : "";
  if (isShowSymbol || isShowName) {
    //是否法币
    const isFlatCoin = isFiat(getDefaultPointType());
    if (isFlatCoin) {
      if (isShowSymbol) {
        const returnStr =
          formatPointInfo.symbol + spaceFlag + formatPointInfo.value;
        return returnStr;
      } else if (isShowName) {
        return formatPointInfo.value + spaceFlag + formatPointInfo.name;
      }
    } else {
      if (isForwardShowSymbol) {
        return (
          (isShowSymbol ? formatPointInfo.symbol : formatPointInfo.name) +
          formatPointInfo.value +
          spaceFlag
        );
      }
      return (
        formatPointInfo.value +
        spaceFlag +
        (isShowSymbol ? formatPointInfo.symbol : formatPointInfo.name)
      );
    }
  } else {
    return formatPointInfo.value;
  }
}

//加密货币转玩家绑定的法币币种或者系统默认配置法币币种
/**
 * Point:基础货币金额 isConverted://是否已是可直接格式化的数值 pointType://未传值-1 默认使用用户绑定币种
 * showSymbol://是否显示符号 虚拟币显示名称 法币显示法币符号
 *
 * -
 */

type FormatToBindPointResult =
  | string
  | { prefix: string; result: string; suffix: string };

export function formatToBindPoint({
  value = 0,
  isConverted = false,
  pointType = -1,
  showSymbol = true,
  showAbbr = false,
  bDeleteZeroPoint = false,
  decimalNonEmphasis = false,
  showUnit = false,
  prev = "",
  formartType = 0,
  isReward = false,
  precision = -1,
  deleteZero = false,
  showPlus = false,
  isFiatPoint = false,
  returnVariable = false,
}: FormatToBindPointParams): FormatToBindPointResult {
  let propsValue =
    typeof value === "number" ? value : parseFloat(value.toString());
  const isLower = propsValue < 0;
  propsValue = Math.abs(propsValue);
  let finalPointType = pointType;

  if (isReward) {
    finalPointType = getRewardPointType();
    if (!isConverted) {
      propsValue = baseToReward(propsValue);
      isConverted = true;
    }
  }

  finalPointType = finalPointType < 0 ? getDefaultPointType() : finalPointType;
  const pointInfo = getCrypto(finalPointType);
  const isFiatValue = isFiatPoint ? isFiatPoint : isFiat(finalPointType);
  const fixed = precision > -1 ? precision : getPointFixed(finalPointType);

  let result = propsValue.toFixed(fixed);
  if (!isConverted) {
    propsValue = pointFormatterOrigin(
      propsValue,
      pointType,
      pointInfo.point_ratio,
      formartType,
    );
  }
  if (isFiatValue) {
    result = pointFormatterInFiat(
      propsValue,
      pointType,
      pointInfo.point_ratio,
      fixed,
      bDeleteZeroPoint,
      showUnit,
    );
  } else {
    result = pointFormatterLocal(propsValue, fixed, bDeleteZeroPoint);
  }

  if (deleteZero) {
    result = result.replace(/(\.\d*?)0+$/, "$1");
  }

  if (isLower) {
    result = "-" + result;
  } else if (showPlus) {
    result = "+" + result;
  }

  let prefix = prev || "";
  let suffix = "";
  if (showAbbr) {
    suffix = ` ${pointInfo.name}`;
  } else if (showSymbol) {
    prefix = isFiatValue ? pointInfo.symbol : prefix;
    suffix = !isFiatValue ? pointInfo.name : suffix;
  }

  if (returnVariable) {
    return {
      prefix,
      result,
      suffix,
    };
  }

  return prefix + result + suffix;
}

// 格式化值 input: 100, output: 1.00
export const formatValue = (
  val?: number,
  toFixed?: number,
): string | number => {
  return val ? (val / 100).toFixed(toFixed) : 0;
};

// 获取币种符号
export const getPointSymbolOrName = (point_type: number): string => {
  const isFiatType = isFiat(point_type);
  if (isFiatType) {
    return getCrypto(point_type).symbol;
  } else {
    return getCrypto(point_type).name;
  }
};
